import React, { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Download, Trash2, Filter as FilterIcon, RefreshCcw, Mail, Phone,
  Building2, Calendar, Save, X,
} from "lucide-react";

const STATUS_OPTS = [
  { v: "new",       l: "New",       cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "contacted", l: "Contacted", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "qualified", l: "Qualified", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "won",       l: "Won",       cls: "bg-purple-100 text-purple-800 border-purple-200" },
  { v: "lost",      l: "Lost",      cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
];
const PLAN_LABEL = {
  starter: "Starter", growth: "Growth", scale: "Scale", enterprise: "Enterprise", not_sure: "Not sure",
};
const UNITS_LABEL = {
  up_to_20: "≤20 units", up_to_100: "≤100 units", up_to_500: "≤500 units", "500_plus": "500+ units",
};

/**
 * AdminDemoRequests — SF-3 / Iter34.
 *
 * Lists every submission from the public Book-a-Demo modal. Admin can:
 *   • filter by status
 *   • change status inline
 *   • add notes inline (persisted on blur)
 *   • delete a row
 *   • export the full queue to CSV
 */
export default function AdminDemoRequests() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/demo-requests${q}`);
      setItems(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const counts = useMemo(() => {
    const c = { total: items.length };
    for (const s of STATUS_OPTS) c[s.v] = 0;
    for (const r of items) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [items]);

  const setStatus = async (r, newStatus) => {
    try {
      await api.put(`/admin/demo-requests/${r.id}`, { status: newStatus });
      setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, status: newStatus } : x));
      toast.success(`Marked as ${newStatus}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const saveNote = async (r) => {
    if ((r.notes || "") === noteDraft) { setEditingNoteId(null); return; }
    try {
      await api.put(`/admin/demo-requests/${r.id}`, { notes: noteDraft });
      setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, notes: noteDraft } : x));
      toast.success("Note saved");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setEditingNoteId(null);
  };
  const remove = async (r) => {
    if (!confirm(`Delete request from ${r.name}?`)) return;
    try {
      await api.delete(`/admin/demo-requests/${r.id}`);
      setItems((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/demo-requests.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "demo-requests.csv";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto space-y-6" data-testid="admin-demo-requests">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-medium">Demo Requests</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Every submission from the public landing page's <em>Book a Demo</em> form. Follow up within one business day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm hover:bg-zinc-50" data-testid="demo-reqs-refresh">
            <RefreshCcw className="h-4 w-4" /> Refresh
          </button>
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="demo-reqs-export">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Status counters + filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setStatusFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs border ${statusFilter === "" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}
          data-testid="demo-reqs-filter-all">
          <FilterIcon className="inline h-3 w-3 me-1" /> All <span className="text-zinc-400 ms-1">({counts.total})</span>
        </button>
        {STATUS_OPTS.map((s) => (
          <button key={s.v} onClick={() => setStatusFilter(s.v)}
            className={`px-3 py-1.5 rounded-full text-xs border ${statusFilter === s.v ? "bg-zinc-900 text-white border-zinc-900" : `${s.cls} border-current/20 hover:opacity-90`}`}
            data-testid={`demo-reqs-filter-${s.v}`}>
            {s.l} <span className="text-current opacity-60 ms-1">({counts[s.v] || 0})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-xl bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Received</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Interest</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Notes</th>
              <th className="px-3 py-2 w-1" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-400 text-sm">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500 text-sm" data-testid="demo-reqs-empty">
                No demo requests yet.
              </td></tr>
            )}
            {!loading && items.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50/40" data-testid={`demo-reqs-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-zinc-600">
                  <div className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-zinc-400" /> {new Date(r.created_at).toLocaleString()}</div>
                  {r.language && r.language !== "en" && <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">{r.language}</div>}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-zinc-500 space-y-0.5 mt-1">
                    <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-zinc-900"><Mail className="h-3 w-3" /> {r.email}</a>
                    {r.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</div>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {r.company ? <span className="inline-flex items-center gap-1 text-xs text-zinc-700"><Building2 className="h-3 w-3 text-zinc-400" /> {r.company}</span> : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-zinc-700">
                  {r.plan_interest && <div>Plan: <strong>{PLAN_LABEL[r.plan_interest] || r.plan_interest}</strong></div>}
                  {r.units_planned && <div className="text-zinc-500">{UNITS_LABEL[r.units_planned] || r.units_planned}</div>}
                  {r.message && <div className="text-[11px] text-zinc-500 italic mt-1 max-w-[240px] truncate" title={r.message}>“{r.message}”</div>}
                </td>
                <td className="px-3 py-2">
                  <StatusPicker current={r.status} onChange={(v) => setStatus(r, v)} testid={`demo-reqs-status-${r.id}`} />
                </td>
                <td className="px-3 py-2">
                  {editingNoteId === r.id ? (
                    <div className="flex gap-1 items-start">
                      <textarea autoFocus rows={2} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                        onBlur={() => saveNote(r)}
                        className="w-full text-xs px-2 py-1 border border-zinc-300 rounded" data-testid={`demo-reqs-note-input-${r.id}`} />
                      <button onClick={() => saveNote(r)} className="p-1 hover:bg-zinc-100 rounded"><Save className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingNoteId(r.id); setNoteDraft(r.notes || ""); }}
                      className="text-xs text-zinc-600 hover:text-zinc-900 text-start block max-w-[220px] truncate" title={r.notes || "Add note"} data-testid={`demo-reqs-note-${r.id}`}>
                      {r.notes ? r.notes : <span className="text-zinc-400 italic">Add note…</span>}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => remove(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Delete" data-testid={`demo-reqs-delete-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPicker({ current, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const opt = STATUS_OPTS.find((s) => s.v === current) || STATUS_OPTS[0];
  return (
    <div className="relative">
      <button onClick={() => setOpen((x) => !x)}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider border ${opt.cls}`}
        data-testid={testid}>
        {opt.l}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 min-w-[130px]" onMouseLeave={() => setOpen(false)}>
          {STATUS_OPTS.map((s) => (
            <button key={s.v} onClick={() => { onChange(s.v); setOpen(false); }}
              className="w-full text-start px-3 py-1.5 text-xs hover:bg-zinc-100">
              <span className={`inline-block h-2 w-2 rounded-full ${s.cls.split(" ")[0]} me-2`} /> {s.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
